<template>
   <Modal
      v-model="model"
      size="md"
      fit-height
      :title="entry ? format.actionLabel(entry.action) : ''"
      :description="description"
   >
      <template #extra-header>
         <HighlightedText v-if="entry" :type="badge" :text="format.typeLabel(entry.type)" />
      </template>

      <div v-if="entry" class="flex flex-col gap-5">
         <dl class="flex flex-col gap-1">
            <div v-for="row in meta" :key="row.label" class="flex items-baseline gap-2">
               <dt class="p-s text-grey w-24 shrink-0">{{ row.label }}</dt>
               <dd class="p-s min-w-0 break-words" :class="row.class">{{ row.value }}</dd>
            </div>
         </dl>

         <div class="flex flex-col gap-2">
            <h5>{{ changesTitle }}</h5>

            <p v-if="!changes.length" class="p-s text-grey">
               {{ $t('web.components.organization-modal.adminlog.no-changes') }}
            </p>

            <div v-else class="__grid">
               <div
                  v-for="change in changes"
                  :key="change.column"
                  class="rounded-xsmall border-light-grey flex flex-col gap-1 border p-2"
               >
                  <p class="p-s text-grey">{{ format.columnLabel(change.column) }}</p>

                  <template v-if="entry.type === 'UPDATE'">
                     <div class="flex items-baseline gap-2">
                        <span class="p-s text-grey w-10 shrink-0">
                           {{ $t('web.components.organization-modal.adminlog.before') }}
                        </span>
                        <span class="p-s text-grey min-w-0 break-words line-through">
                           {{ format.value(change.old, VALUE_LENGTH) }}
                        </span>
                     </div>
                     <div class="flex items-baseline gap-2">
                        <span class="p-s text-grey w-10 shrink-0">
                           {{ $t('web.components.organization-modal.adminlog.after') }}
                        </span>
                        <span class="p-s text-primary min-w-0 break-words">
                           {{ format.value(change.new, VALUE_LENGTH) }}
                        </span>
                     </div>
                  </template>

                  <span v-else class="p-s min-w-0 break-words">
                     {{
                        format.value(
                           entry.type === 'DELETE' ? change.old : change.new,
                           VALUE_LENGTH
                        )
                     }}
                  </span>
               </div>
            </div>
         </div>
      </div>
   </Modal>
</template>

<script lang="ts" setup>
import type { Unpacked } from '@xleddyl/types'
import { useAdminLogFormat } from '~/composables/adminlog'
import type { useAdminlog } from '~/composables/api/queries/adminlog'

type Entry = Unpacked<NonNullable<ReturnType<typeof useAdminlog>['data']['value']>>

type Props = {
   entry?: Entry
}

const VALUE_LENGTH = 200

const props = defineProps<Props>()
const model = defineModel<boolean>()

const { t } = useI18n()
const { $dayjs } = useNuxtApp()
const format = useAdminLogFormat()

const changes = computed(() => props.entry?.changes ?? [])

const badge = computed(() => {
   switch (props.entry?.type) {
      case 'CREATE':
         return 'success'
      case 'UPDATE':
         return 'warning'
      case 'DELETE':
         return 'error'
      default:
         return 'light-grey'
   }
})

const description = computed(() =>
   props.entry ? $dayjs(props.entry.created_at).format('DD MMM YYYY HH:mm') : ''
)

const meta = computed(() => [
   {
      label: t('grid.columns.table_name'),
      value: format.tableLabel(props.entry?.table_name),
   },
   {
      label: t('grid.columns.record_id'),
      value: props.entry?.record_id || '-',
      class: 'font-mono',
   },
   {
      label: t('grid.columns.user_email'),
      value: format.userLabel(props.entry?.user_email),
   },
   {
      label: t('grid.columns.user_role'),
      value: props.entry?.user_role ? t(`enum.${props.entry.user_role}`) : '-',
   },
])

const changesTitle = computed(() => {
   switch (props.entry?.type) {
      case 'CREATE':
         return t('web.components.organization-modal.adminlog.changes-create')
      case 'DELETE':
         return t('web.components.organization-modal.adminlog.changes-delete')
      default:
         return t('web.components.organization-modal.adminlog.changes-update')
   }
})
</script>
